import { Button } from "components/DesignSystem/Button/Button";
import { Link } from "components/DesignSystem/Link/Link";
import Modal from "components/DesignSystem/Modal/Modal";
import { ArrowUpRight } from "components/icons/ArrowUpRight";
import { useToast } from "hooks/useToast";
import {
  TermsOfService,
  useAcceptTermsOfServiceMutation,
} from "store/apis/termsOfService";
import { BackendError } from "types/utils/error";
import { formatDate } from "utils/formatDate";

type TermsOfServiceModalProps = {
  termsOfService: TermsOfService;
};

export const TermsOfServiceModal = ({
  termsOfService,
}: TermsOfServiceModalProps) => {
  const { alertToast, successToast } = useToast();
  const [acceptTermsOfService, { isLoading }] =
    useAcceptTermsOfServiceMutation();

  const handleAccept = async () => {
    try {
      await acceptTermsOfService({
        termsOfServiceId: termsOfService.uuid,
      }).unwrap();
      successToast({ message: "Terms of Service accepted" });
    } catch (error) {
      alertToast({
        message:
          (error as BackendError)?.data?.error?.message ??
          "Could not record your acceptance. Please try again.",
      });
    }
  };

  const blockDismiss = (event: Event) => event.preventDefault();

  return (
    <Modal.Root open>
      <Modal.Content
        onEscapeKeyDown={blockDismiss}
        onInteractOutside={blockDismiss}
      >
        <Modal.Header>
          <Modal.Title className="t-mb-0 t-text-title-h3">
            We've updated our Terms of Service
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="t-flex t-flex-col t-gap-4">
            <p className="t-m-0 t-text-body t-text-text-60">
              Please review the updated terms that govern your use of Inkle.
              You'll need to accept them to continue using your account.
            </p>

            <div className="t-flex t-gap-8 t-rounded-md t-border t-border-solid t-border-neutral-0 t-bg-surface-lighter-grey t-px-4 t-py-3">
              <div className="t-flex t-flex-col t-gap-1">
                <span className="t-text-body-sm t-text-text-30">
                  Effective from
                </span>
                <span className="t-text-subtext t-text-text-100">
                  {formatDate(termsOfService.effective_date)}
                </span>
              </div>

              <div className="t-flex t-flex-col t-gap-1">
                <span className="t-text-body-sm t-text-text-30">Version</span>
                <span className="t-text-subtext t-text-text-100">
                  {termsOfService.version}
                </span>
              </div>
            </div>

            <Link
              to={termsOfService.document_url}
              target="_blank"
              rel="noreferrer"
              className="t-flex t-w-fit t-items-center t-gap-1.5 t-text-button"
            >
              Read our updated Terms & Conditions, and Privacy Policy
              <ArrowUpRight color="currentColor" />
            </Link>
          </div>
        </Modal.Body>
        <Modal.FooterButtonGroup>
          <Button
            customType="primary"
            size="small"
            type="button"
            onClick={handleAccept}
            isLoading={isLoading}
            disabled={isLoading}
          >
            Accept &amp; continue
          </Button>
        </Modal.FooterButtonGroup>
      </Modal.Content>
    </Modal.Root>
  );
};
